import * as THREE from 'three';
import type { SketchFace, SketchSolid, SketchVec3 } from './types';
import { pointKey } from './topology';

/**
 * Face orientation ops (Sketch's Reverse Faces / Orient Faces / Flip Along).
 *
 * The shell convention (types.ts): a face's `outer` loop is CCW seen from
 * the normal side, holes are CW, and the stored `normal` is the front side.
 * Reversing a face therefore always flips BOTH the normal and every loop;
 * mirroring a solid reverses loop winding but leaves the outward normals
 * pointing outward (they mirror with the geometry), so Flip Along only
 * rewinds loops.
 *
 * Exact-curve metadata (loopMeta) encodes the original winding through its
 * angle range, so any operation that rewinds loops drops it — the face
 * degrades to its polyline boundary, which stays renderable and editable.
 */

/** Reverse one face: negate the normal and rewind every loop. */
export function reverseFace(face: SketchFace): SketchFace {
    return {
        ...face,
        normal: [
            -face.normal[0],
            -face.normal[1],
            -face.normal[2],
        ] as SketchVec3,
        outer: [...face.outer].reverse(),
        holes: face.holes.map((h) => [...h].reverse()),
        loopMeta: undefined,
    };
}

/** Undirected key so the two faces sharing an edge find each other. */
const undirectedKey = (a: SketchVec3, b: SketchVec3): string => {
    const ka = pointKey(a);
    const kb = pointKey(b);
    return ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
};

/**
 * Make the faces of one solid consistently oriented (Sketch's Orient
 * Faces): adjacent faces must traverse their shared edge in OPPOSITE
 * directions. A BFS over the edge adjacency assigns flips per face; faces
 * in components reachable from face 0 orient against it, unreachable faces
 * (open shells / non-manifold edges) keep their current orientation.
 * Returns a new faces array (ids stable) and how many faces flipped.
 */
export function orientSolidFaces(faces: SketchFace[]): { faces: SketchFace[]; flipped: number } {
    // edges: undirected key -> every (face, traversal direction) pair.
    const edges = new Map<string, Array<{ faceIdx: number; forward: boolean }>>();
    faces.forEach((face, faceIdx) => {
        const walk = (loop: SketchVec3[]): void => {
            for (let i = 0; i < loop.length; i++) {
                const a = loop[i];
                const b = loop[(i + 1) % loop.length];
                const key = undirectedKey(a, b);
                const list = edges.get(key) ?? [];
                // 'forward' records the traversal against the canonical
                // (lexicographically smaller endpoint first) direction.
                list.push({ faceIdx, forward: pointKey(a) < pointKey(b) });
                edges.set(key, list);
            }
        };
        walk(face.outer);
        face.holes.forEach(walk);
    });

    // BFS orientation assignment: sign 0 keeps the face, 1 flips it. The
    // consistency rule for a shared edge is forward(f) XOR sign(f) XOR
    // forward(g) XOR sign(g) === true (opposite traversal after adjusting).
    const sign = new Map<number, number>();
    let flipped = 0;
    for (let start = 0; start < faces.length; start++) {
        if (sign.has(start)) continue;
        sign.set(start, 0);
        const queue = [start];
        while (queue.length > 0) {
            const current = queue.shift()!;
            for (const [key, owners] of edges) {
                const mine = owners.find((e) => e.faceIdx === current);
                if (!mine) continue;
                for (const other of owners) {
                    if (other.faceIdx === current) continue;
                    if (sign.has(other.faceIdx)) continue;
                    // sign(other) makes the traversals opposite.
                    const next = (mine.forward === other.forward ? 1 : 0) ^ (sign.get(current) ?? 0);
                    sign.set(other.faceIdx, next);
                    queue.push(other.faceIdx);
                }
            }
        }
    }

    const out = faces.map((face, idx) => {
        if (sign.get(idx) !== 1) return face;
        flipped++;
        return reverseFace(face);
    });
    return { faces: out, flipped };
}

/**
 * Flip a solid along a world axis through its own center (Sketch's Flip
 * Along Red/Green/Blue): mirror every point, keep the mirrored outward
 * normals, and rewind the loops so the CCW-from-normal-side convention
 * survives the mirror.
 */
export function flipSolidAlong(solid: SketchSolid, axis: 'x' | 'y' | 'z'): SketchSolid {
    const box = new THREE.Box3();
    solid.faces.forEach((face) => {
        const walk = (loop: SketchVec3[]): void =>
            loop.forEach((p) => box.expandByPoint(new THREE.Vector3(...p)));
        walk(face.outer);
        face.holes.forEach(walk);
    });
    if (box.isEmpty()) return solid;
    const center = box.getCenter(new THREE.Vector3());
    const scale = new THREE.Vector3(
        axis === 'x' ? -1 : 1,
        axis === 'y' ? -1 : 1,
        axis === 'z' ? -1 : 1,
    );
    const mirror = new THREE.Matrix4()
        .makeTranslation(center.x, center.y, center.z)
        .multiply(new THREE.Matrix4().makeScale(scale.x, scale.y, scale.z))
        .multiply(new THREE.Matrix4().makeTranslation(-center.x, -center.y, -center.z));

    const mapPoint = (p: SketchVec3): SketchVec3 => {
        const v = new THREE.Vector3(...p).applyMatrix4(mirror);
        return [v.x, v.y, v.z];
    };
    const mapNormal = (p: SketchVec3): SketchVec3 => {
        const v = new THREE.Vector3(...p).transformDirection(mirror).normalize();
        return [v.x, v.y, v.z];
    };

    return {
        ...solid,
        faces: solid.faces.map((face) => ({
            ...face,
            origin: mapPoint(face.origin),
            normal: mapNormal(face.normal),
            outer: [...face.outer].map(mapPoint).reverse(),
            holes: face.holes.map((h) => [...h].map(mapPoint).reverse()),
            loopMeta: undefined,
        })),
    };
}
